<script setup lang="ts">
/**
 * TODO : Click and release on button creates a marker behind button. it should not.
 */
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useLongPressTooltip } from "@/composable/useLongPressTooltip";
import { useDraggable } from "@vue-dnd-kit/core";
import type { PhotoDragData } from "@/types/dnd.types";
import { Facade, DraggerType } from "@/drawing";
import DraggerMenuState from "@/stores/DraggerMenuState";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import { Z_INDEX_TOOLBAR } from "../layouts/constants";
import { canvasTopForIcons } from "@/stores/CanvasLayoutState";
import PhotoMarkerToggleShape from "./options/PhotoMarkerToggleShape.vue";
import PhotoMarkerInput from "./options/PhotoMarkerInput.vue";

withDefaults(
  defineProps<{
    height?: string;
    fullscreen: boolean;
  }>(),
  {
    height: "auto",
  },
);

const { refHook } = useLongPressTooltip();

// Draggable setup (unchanged)
const { elementRef, isDragging, handleDragStart } = useDraggable({
  groups: ["canvas-entity"],
  data: computed(() => ({ type: "photo" }) satisfies PhotoDragData),
});

// Photo number management (from PhotoMarkerInput)
const photoNumber = ref("1");

// The number input drives the internal sequence counter, which is meaningless
// to expose once the legend displays a date/time instead of that number.
const isPhotoMarkerNumberEditable = computed(() => {
  return DraggerMenuState.getPhotoMarkerAttribute() !== "date";
});

function setActivePhotoMarkerNumber() {
  if (!Facade.isInitialized) return;
  const num = parseInt(photoNumber.value) || 1;
  Facade.photoMarkers.currentNumber = num;
}

function syncFromFacade() {
  if (!Facade.isInitialized) return;
  photoNumber.value = Facade.photoMarkers.currentNumber.toString();
}

watch(
  () => photoNumber.value,
  (newValue) => {
    if (!newValue) photoNumber.value = "0";
  },
);

watch(
  () => DraggerMenuState.activeDragger.name,
  (newName, oldName) => {
    if (newName == DraggerType.PhotoMarker && newName != oldName) {
      setActivePhotoMarkerNumber();
    }
  },
);

let unsubscribeFileOpen: (() => void) | undefined;
let unsubscribePhotoMarkers: (() => void) | undefined;

function subscribeToChanges() {
  if (unsubscribePhotoMarkers) return;
  unsubscribePhotoMarkers = Facade.photoMarkers.onChange(() => {
    syncFromFacade();
  });
}

onMounted(() => {
  unsubscribeFileOpen = Facade.onFileOpen(() => {
    syncFromFacade();
    subscribeToChanges();
  });

  if (Facade.isInitialized) {
    syncFromFacade();
    subscribeToChanges();
  }
});

onBeforeUnmount(() => {
  unsubscribeFileOpen?.();
  unsubscribePhotoMarkers?.();
});
</script>

<template>
  <!-- Une seule barre flottante : poignée de la photo, forme du marqueur, numéro -->
  <div
    v-if="!fullscreen"
    ref="refHook"
    class="photo-bar position-absolute start-50 translate-middle-x canvas-icon-scaled"
    :style="{
      top: `${canvasTopForIcons + 8}px`,
      zIndex: Z_INDEX_TOOLBAR,
    }"
    :data-bs-content="$t('canvas:tools.photomarker.tooltip.text')"
    :data-bs-title="$t('canvas:tools.photo.tooltip.title')"
    @click.stop
  >
    <button
      ref="elementRef"
      type="button"
      class="photo-drag-handle"
      :class="{ dragging: isDragging }"
      :aria-label="$t('canvas:tools.photo.tooltip.title')"
      @pointerdown="handleDragStart"
    >
      <PhosphorIcon
        name="dots-six-vertical"
        :size="16"
        weight="bold"
        class="opacify-30"
      />
      <PhosphorIcon name="camera" :size="16" weight="bold" />
    </button>

    <PhotoMarkerToggleShape />

    <PhotoMarkerInput v-if="isPhotoMarkerNumberEditable" />
  </div>
</template>

<style lang="scss" scoped>
.photo-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px;
  border: 1px solid var(--canvas-border);
  border-radius: 12px;
  background-color: var(--canvas-bg);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

// Ce qui se glisse sur le plan : la teinte de la sélection le désigne.
.photo-drag-handle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  width: 52px;
  height: 32px;
  padding: 0;
  border: 1px solid var(--canvas-selection-border);
  border-radius: 8px;
  background:
    linear-gradient(var(--canvas-selection-bg), var(--canvas-selection-bg)),
    var(--canvas-bg);
  box-shadow: 0 0 12px var(--canvas-selection-glow);
  color: #fff;
  white-space: nowrap;
  cursor: grab;
  touch-action: none;

  &.dragging {
    opacity: 0.5;
  }
}
</style>
